<template lang="pug">
y-dialog(v-if="showDialog" v-model="showDialog" max-width=568 fullscreen persistent fluid scrollable content-class="round-2xl")
  v-card.neutral200
    //- Top bar
    v-card-title.px-4
      y-button(outlined prepend-icon="mdi-arrow-left" color="neutral400" icon-color="neutral700" icon-size="20" size="xs" unset-min-width button-class="px-1" @click="closeDialog")
      div.text-xl.font-semibold.ml-4 Database settings
      v-spacer
      y-button(label="Done" size="sm" @click="closeDialog")

    v-card-text.neutral300.pa-0
      v-container.pa-6(style="max-width: 1000px;")
        template(v-if="isFetching")
          v-skeleton-loader.pa-4(type="list-item-three-line@6")
        v-card.round-xl.pa-6(v-else outlined)
          div.d-flex.align-center
            div
              .text-md.font-medium.neutral700--text Custom information fields
              .text-sm.neutral700--text.font-normal Manage additional information fields for patient registration
            v-spacer
            y-button(label="Add field" unset-min-width size="sm" @click="showAddCustomFieldDialog = true")
          .data-table.mt-4
            v-data-table(ref="table" :headers="headers" :items="fetchingData" item-key="id" hide-default-footer item-class="text-sm" disable-sort :header-props="{ sortIcon: '$yzi-triangle' }" disable-pagination)
              template(#no-data)
                .d-flex.align-center.justify-center.py-4
                  .text-sm.neutral700--text no fields added yet
              template(v-slot:body="props")
                draggable(v-if="isTableMounted && $refs.table" :list="fetchingData" tag="tbody" handle=".handle" draggable=".can-drag" @start="drag = true" @end="endDrag")
                  v-nodes(:vnodes="$refs.table.genItems(props.items, props)")
              template(v-slot:item="{ item, headers, index }")
                tr.primary--text.can-drag
                  template(v-for="header in headers")
                    template(v-if="header.value === 'handle'")
                      td.py-4
                        div.d-flex(v-if="item.default")
                          v-icon.ma-0(color="neutral300") $yzi-dots-grid-02
                        div.d-flex.handle.cursor-grab(v-else)
                          v-icon.ma-0(color="neutral500") $yzi-dots-grid-02
                    template(v-else-if="header.value === 'actions'")
                      td.py-4
                        div.d-flex.align-center.justify-end
                          v-btn.button-xs.mr-1(color="neutral700" dark outlined @click="editItem(item, index)") {{ $t('edit') }}
                          v-btn.button-xs(icon color="neutral700" :disabled="item.default" @click="deleteItem(item, index)")
                            v-icon(size="20") $yzi-trash-03
                    template(v-else-if="header.value === 'field'")
                        td.py-4
                          .text-sm.neutral700--text {{ item.extra_options.display_name || item.field_name }}
                    template(v-else-if="header.value === 'type'")
                      td.py-4
                        .d-flex.align-center
                          div.field-icon
                            v-icon.ma-1(size="16" color="neutral100") {{ getIcon(item.datatype) }}
                          .text-xs.font-normal.ml-2 {{ types[item.datatype] }}
                    template(v-else-if="header.value === 'required'")
                      td.py-4
                        .d-flex.align-center
                          template(v-if="item.extra_options.required")
                            v-icon.mr-1(size="20" color="success500") mdi-check-circle
                            .text-xs.font-normal {{ $t('on') }}
                          template(v-else)
                            v-icon.mr-1(size="20" color="neutral500") mdi-check-circle
                            .text-xs.font-normal {{ $t('off') }}
                    template(v-else-if="header.value === 'status'")
                      td.py-4
                        template(v-if="item.status === 'active'")
                          v-btn.button-xs.button-xs-spacing(depressed color="success100" :loading="item.isLoading" @click="toggleStatus(item)")
                            .d-flex.align-center
                              v-icon.mr-1(size="8" color="success700") mdi-circle
                              .success700--text.font-normal.text-sm {{ $t('active') }}
                        template(v-else)
                          v-btn.button-xs.button-xs-spacing(depressed color="danger100" :loading="item.isLoading" @click="toggleStatus(item)")
                            .d-flex.align-center
                              v-icon.mr-1(size="8" color="danger700") mdi-circle
                              .danger700--text.font-normal.text-sm {{ $t('inactive') }}
                    template(v-else)
                      td.py-4.text-sm {{ item[header.id.toLowerCase()] }}
    .dialogs
      add-custom-field-dialog(v-if="showAddCustomFieldDialog" :open.sync="showAddCustomFieldDialog" :allFields="fetchingData" :field="selectedItem" @closed="resetItem" @updated="mapData")
      delete-confirmation-dialog(v-if="showDeleteConfirmationDialog" :open.sync="showDeleteConfirmationDialog" :loading="isDeleting" @cancel="resetItem" @delete="deleteAction")
        .text-lg.font-medium Are you sure you want to remove this custom field?
        .text-md.mt-4 This action will be done immediately and can’t be undone. Click “Confirm” to proceed

</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import AddCustomFieldDialog from '@/components/clinic/patient/AddCustomFieldDialog.vue'
import DeleteConfirmationDialog from '@/components/clinic/dialogs/DeleteConfirmationDialog.vue'
import draggable from 'vuedraggable'

export default {
  components: {
    AddCustomFieldDialog, DeleteConfirmationDialog, draggable,
    VNodes: {
      functional: true, 
      render: (h, ctx) => ctx.props.vnodes,
    }
  },
  props: ['open', 'segment'],
  data: () => ({
    isFetching: false,
    isDeleting: false,
    isTableMounted: false,
    showAddCustomFieldDialog: false,
    showDeleteConfirmationDialog: false,
    drag: false,
    selectedIndex: null,
    selectedItem: null,
    fetchingData: [],
    types:{
      string: 'Short answer', 
      selection: 'Multiple choice',
      phone: 'Phone number',
      number: 'Number only', 
      address: 'Address',
      text: 'Paragraph',
      date: 'Date',
      email: 'Email'
    },
  }),
  computed: {
    ...mapGetters({
      company: 'getCompany',
    }),
    showDialog: {
      get () {
        return this.open
      },
      set (newValue) {
        this.$emit('update:open', newValue)
      }
    },
    headers() {
      const fields = [
        { id: 'handle', field_name: '', enable: true },
        { id: 'field', field_name: 'field', enable: true },
        { id: 'type', field_name: 'type', enable: true },
        { id: 'required', field_name: 'required', enable: true },
        { id: 'status', field_name: 'status', enable: true },
      ]
      fields.map(item => {
        item.text = item.field_name
        item.value = item.id
        item['class'] = 'text-xs font-bold neutral500--text text-uppercase'
      })
      const actions = {
        text: 'Action',
        value: 'actions',
        enable: true,
        class: 'text-xs font-bold text-end neutral500--text text-uppercase',
        sortable: false
      }
      return [...fields, actions]
    },
  },
  mounted () {
    this.fetchFormFields()
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchFormFields() {
      this.isFetching = true
      let params = {
        page_size: -1,
      }
      this.$axios.get(`/clinics/${this.company.slug}/patient_custom_fields`, params)
        .then(({ data }) => {
          if (data && data.results) {
            this.fetchingData = data.results
          }
        })
        .catch(() => {})
        .finally(() => {
          this.isFetching = false
          this.$nextTick(() => {
            this.isTableMounted = true
          })
        })
    },
    mapData(data) {
      this.fetchingData.splice(this.fetchingData.findIndex(f => f.id === data.id), 1, data)
    },
    resetItem() {
      this.selectedItem = null
      this.selectedIndex = null
    },
    editItem(item, index) {
      this.selectedIndex = index
      this.selectedItem = item
      this.showAddCustomFieldDialog = true
    },
    deleteItem(item, index) {
      this.selectedIndex = index
      this.selectedItem = item
      this.showDeleteConfirmationDialog = true
    },
    async endDrag (event) {
      console.log(event)
      this.drag = false
      const newPosition = event.newIndex
      const item = this.fetchingData[newPosition]
      try {
        await this.$axios.post(`/clinics/${this.company.slug}/patient_custom_fields/${item.id}/move`, { position: newPosition })
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.error_move_item', { item: 'field' }) ])
      }
    },
    async deleteAction() {
      this.isDeleting = true
      const index = this.selectedIndex
      const item = this.selectedItem
      try {
        await this.$axios.delete(`/clinics/${this.company.slug}/patient_custom_fields/${item.id}`)
        this.fetchingData.splice(index, 1)
        this.showMessage(['success', this.$t('success_message.delete_successful')])
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isDeleting = false
        this.showDeleteConfirmationDialog = false
        this.resetItem()
      }
    },
    async toggleStatus(item) {
      this.$set(item, 'isLoading', true)
      let data = { ...item }
      let status = item.status
      if (status === 'active') {
        data.status = 'inactive'
      } else {
        data.status = 'active'
      }
      try {
        const resp = await this.$axios.patch(`/clinics/${this.company.slug}/patient_custom_fields/${item.id}`, data)
        this.mapData(resp.data)
        this.showMessage(['success', this.$t('success_message.update_successful')])
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.$set(item, 'isLoading', false)
        this.showDeleteConfirmationDialog = false
      }
    },
    getIcon (type) {
      switch (type) {
        case 'string':
          return '$yzi-menu-05'
        case 'selection':
          return '$yzi-dotpoints-02'
        case 'phone':
          return '$yzi-phone-02'
        case 'number':
          return '$yzi-123'
        case 'address':
          return '$yzi-markerpin-02'
        case 'text':
          return '$yzi-menu-03'
        case 'date':
          return '$yzi-calendar'
        case 'email':
          return '$yzi-mail-02'
        default:
          return '$yzi-menu-05'
      }
    },
    closeDialog () {
      this.$emit('closed')
      this.showDialog = false
    }
  }
}
</script>

<style lang="scss" scoped>
.field-icon {
  display: inline-block;
  background: #333;
  border-radius: 4px;
  width: 24px;
  height: 24px;
}
</style>